<template lang="pug">
  #messages_page.container.page--messages
    .box.has-shadow
      .columns
        .column
          h3.title.has-text-left Messages
        .column.is-narrow
          router-link.button.is-small.is-outlined(:to="{name: 'MessageSettings'}")
            b-icon(icon="settings" size="is-small")
            span Settings

      //- EXPLANATIONS
      article.message.is-light(v-if="!loading && !threads.length && !currentUserId")
        .message-body
          | You can write to anyone you have shared a lesson with. Open a contact
          | and start the conversation — they will get an email letting them know.
          br
          router-link.is-underlined(:to="{name: 'People'}") Go to my contacts

      .columns.messages-layout(v-else)
        //- THREAD LIST
        .column.is-4.thread-list
          b-loading(:active="loading" :is-full-page="false")
          ul.menu-list
            li(v-for="thread in threads" :key="thread.user.id")
              router-link.thread-link(
                :to="{name: 'MessageThread', params: {user_id: thread.user.id}}"
                :class="{'is-active': isCurrent(thread.user.id)}"
              )
                .valign.is-flex
                  user-picture.mr-2(:user="thread.user")
                  .thread-summary
                    .valign.is-flex
                      strong.thread-name {{ thread.user.first_name }}
                      b-tag.ml-2(v-if="thread.unread_count" type="is-danger" rounded)
                        | {{ thread.unread_count }}
                      span.ml-auto.is-size-7.has-text-grey
                        | {{ thread.last_message.created_at | moment("DD.MM") }}
                    p.is-size-7.has-text-grey.thread-preview
                      span(v-if="thread.last_message.is_mine") You:&nbsp;
                      | {{ thread.last_message.body }}

        //- THREAD
        .column.thread(v-if="currentUserId")
          .thread-header.valign.is-flex.pb-2
            user-picture.mr-2(:user="thread.user")
            strong {{ thread.user.first_name }} {{ thread.user.last_name }}
            .ml-auto
              router-link.button.is-small.is-outlined.mr-1(
                v-if="thread.user.id"
                :to="thread.user.is_teacher ? `/teachers/${thread.user.id}?tab=lessons` : `/students/${thread.user.id}`"
              )
                b-icon(icon="remove-red-eye" size="is-small")
                span Lessons
              b-button.is-small.is-outlined(type="is-danger" @click="confirmBlock")
                | Block

          .thread-messages(ref="messages")
            b-loading(:active="threadLoading" :is-full-page="false")
            .message-bubble(
              v-for="message in thread.messages"
              :key="message.id"
              :class="message.is_mine ? 'is-mine' : 'is-theirs'"
            )
              p.message-body-text {{ message.body }}
              span.is-size-7.has-text-grey.message-date
                | {{ message.created_at | moment("DD.MM HH:mm") }}

          //- COMPOSER
          form.thread-composer(@submit.prevent="send")
            b-field(:message="composerError" :type="composerError ? 'is-danger' : ''")
              b-input(
                v-model="draft"
                type="textarea"
                rows="3"
                maxlength="5000"
                placeholder="Write a message…"
                :disabled="sending"
                @keydown.native.enter.exact.prevent="send"
              )
            .valign.is-flex
              p.is-size-7.has-text-grey
                | Your email address stays private unless you reply to their
                | notification email.
              b-button.ml-auto(
                native-type="submit"
                type="is-primary"
                :loading="sending"
                :disabled="!draft.trim()"
              )
                | Send

        .column.thread.has-text-grey.is-flex.thread-empty(v-else)
          p Pick a conversation on the left.
</template>


<script>
  import { mapGetters } from 'vuex'
  import UserPictureUI from '@/components/UserPictureUI'

  export default {
    name: 'MessagesPage',
    components: {
      userPicture: UserPictureUI,
    },
    data() {
      return {
        loading: false,
        threadLoading: false,
        sending: false,
        draft: '',
        composerError: null,
      }
    },
    computed: {
      ...mapGetters(['messageThreads', 'messageThread', 'apiUrl', 'user']),
      threads() {
        return this.messageThreads
      },
      thread() {
        return this.messageThread
      },
      currentUserId() {
        return this.$route.params.user_id
      },
    },
    watch: {
      // Switching conversations keeps the same component mounted, so the thread
      // has to be reloaded on the route param.
      currentUserId() {
        this.draft = ''
        this.composerError = null
        this.loadThread()
      },
    },
    created() {
      this.$store.dispatch('setLayout', 'logged-in-layout')
    },
    mounted() {
      this.loading = true
      this.$store.dispatch('getMessageThreads').then(() => {
        this.loading = false
      })
      this.loadThread()
    },
    methods: {
      isCurrent(userId) {
        return `${userId}` === `${this.currentUserId}`
      },
      loadThread() {
        if (!this.currentUserId) return
        this.threadLoading = true
        this.$store.dispatch('getMessageThread', this.currentUserId).then(() => {
          this.threadLoading = false
          this.scrollToBottom()
          // Reading the thread is what stops the notification email, so it has
          // to happen as soon as it is on screen.
          this.$store.dispatch('markThreadRead', this.currentUserId)
        }, () => {
          this.threadLoading = false
        })
      },
      scrollToBottom() {
        this.$nextTick(() => {
          const el = this.$refs.messages
          if (el) el.scrollTop = el.scrollHeight
        })
      },
      send() {
        const body = this.draft.trim()
        if (!body || this.sending) return

        this.sending = true
        this.composerError = null
        this.$store.dispatch('sendMessage', {recipient_id: this.currentUserId, body}).then(() => {
          this.draft = ''
          this.sending = false
          this.scrollToBottom()
          this.$store.dispatch('getMessageThreads')
        }, (res) => {
          this.sending = false
          this.composerError = (res.body && res.body.message)
            || 'Your message could not be sent.'
        })
      },
      confirmBlock() {
        this.$buefy.dialog.confirm({
          title: 'Block this person',
          message: `${this.thread.user.first_name} will no longer be able to send you messages. `
            + 'They are not told they have been blocked. You can undo this in your message settings.',
          confirmText: 'Block',
          type: 'is-danger',
          onConfirm: () => {
            this.$store.dispatch('blockUserFromMessaging', this.thread.user.id).then(() => {
              this.$buefy.toast.open({message: 'Blocked', type: 'is-success'})
            })
          },
        })
      },
    },
  }
</script>


<style lang="scss">
  @import 'colors';

  .page--messages {
    .messages-layout {
      min-height: 60vh;
    }

    .thread-list {
      border-right: 1px solid #ededed;
      max-height: 70vh;
      overflow-y: auto;
      position: relative;
    }

    .thread-link {
      display: block;
      padding: 0.5rem;
      &.is-active {
        background-color: rgba($primary, 0.08);
      }
    }

    // Keep avatars small in the message panel — the global `.avatar img`
    // rule (98px) is meant for profile headers, not conversation rows.
    $avatar: 44px;
    .avatar {
      flex: 0 0 auto;
      img {
        width: $avatar;
        height: $avatar;
        padding: 2px;
      }
      &.letter-avatar {
        width: $avatar;
        height: $avatar;
        line-height: $avatar;
        span {
          width: $avatar - 6px;
          height: $avatar - 6px;
          line-height: $avatar - 6px;
          font-size: 18px;
        }
      }
    }

    .thread-summary {
      min-width: 0;
      flex: 1;
    }

    .thread-name,
    .thread-preview {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .thread {
      display: flex;
      flex-direction: column;
    }

    .thread-empty {
      align-items: center;
      justify-content: center;
    }

    .thread-header {
      border-bottom: 1px solid #ededed;
      align-items: center;
    }

    .thread-messages {
      flex: 1;
      max-height: 50vh;
      overflow-y: auto;
      padding: 1rem 0.5rem;
      position: relative;
    }

    .message-bubble {
      max-width: 75%;
      margin-bottom: 0.75rem;
      padding: 0.5rem 0.75rem;
      border-radius: 8px;
      text-align: left;

      &.is-mine {
        margin-left: auto;
        background-color: rgba($primary, 0.12);
      }

      &.is-theirs {
        background-color: #f5f5f5;
      }
    }

    .message-body-text {
      white-space: pre-wrap;
      word-break: break-word;
    }

    .thread-composer {
      border-top: 1px solid #ededed;
      padding-top: 0.75rem;
    }
  }

  @media screen and (max-width: 768px) {
    .page--messages {
      .thread-list {
        border-right: none;
        max-height: none;
      }
    }
  }
</style>
